import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import NewsCard from "@/components/feature/NewsCard";
import ImageLightbox from "@/components/feature/ImageLightbox";
import useSEO from "@/hooks/useSEO";
import { news } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = news.find((n) => n.id === id);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [shareTip, setShareTip] = useState("");

  useSEO({
    title: article ? `${article.title} - 糖心VLGO资讯` : "资讯不存在 - 糖心VLGO",
    description: article
      ? article.summary
      : "未找到对应的糖心VLGO资讯内容。",
    keywords: article
      ? `${article.title},糖心VLGO,${article.category},糖心VLGO资讯`
      : "糖心VLGO资讯",
  });

  const related = useMemo(() => {
    if (!article) return [];
    const same = news.filter((n) => n.id !== article.id && n.category === article.category);
    const others = news.filter((n) => n.id !== article.id && n.category !== article.category);
    return [...same, ...others].slice(0, 3);
  }, [article]);

  const handleCopyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setShareTip("链接已复制，可以粘贴分享给朋友");
        setTimeout(() => setShareTip(""), 2200);
      })
      .catch(() => {
        setShareTip("链接复制失败，请手动复制地址栏链接");
        setTimeout(() => setShareTip(""), 2200);
      });
  };

  if (!article) {
    return (
      <SiteLayout>
        <div className="w-full min-h-[70vh] flex flex-col items-center justify-center text-center px-4 pt-24">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-article-line text-4xl leading-none"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
            资讯不存在
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            抱歉，没有找到对应的资讯内容，可能链接已失效。
          </p>
          <Link
            to="/news"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line leading-none"></i>
            返回产品资讯
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-10 pt-28 pb-6 bg-background-50">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line leading-none"></i>
          <Link to="/news" className="hover:text-primary-600 transition-colors">
            产品资讯
          </Link>
          <i className="ri-arrow-right-s-line leading-none"></i>
          <span className="text-foreground-900 line-clamp-1">{article.title}</span>
        </nav>
      </div>

      <article className="w-full px-4 md:px-10 pb-12 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <span className="inline-flex px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
            {article.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight tracking-tight">
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line leading-none"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line leading-none"></i>
              {article.date}
            </span>
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 cursor-pointer transition-colors hover:bg-primary-100 hover:text-primary-700"
            >
              <i className="ri-share-forward-line leading-none"></i>
              分享链接
            </button>
          </div>
          {shareTip && (
            <p className="mt-3 text-xs text-primary-600 bg-primary-50 rounded-md px-3 py-2 inline-block">
              {shareTip}
            </p>
          )}

          <div className="mt-8 w-full h-[260px] md:h-[400px] rounded-lg overflow-hidden bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} 封面图`}
              title={`${article.title} - 糖心VLGO资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-10 flex flex-col gap-6">
            {article.content.map((block, idx) => {
              if (block.type === "heading") {
                return (
                  <h2
                    key={idx}
                    className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 tracking-tight mt-2"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "paragraph") {
                return (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {block.text}
                  </p>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote
                    key={idx}
                    className="border-l-4 border-accent-500 bg-accent-50 rounded-r-lg px-6 py-5 text-sm md:text-base text-foreground-800 italic leading-relaxed"
                  >
                    {block.text}
                  </blockquote>
                );
              }
              return (
                <figure key={idx} className="w-full">
                  <div
                    className="w-full h-[240px] md:h-[380px] rounded-lg overflow-hidden bg-background-100 cursor-zoom-in"
                    onClick={() => setLightbox(block.src)}
                  >
                    <img
                      src={block.src}
                      alt={block.alt}
                      title={`${article.title} - 糖心VLGO资讯配图`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <figcaption className="mt-2 text-center text-xs text-foreground-500">
                    {block.alt}（点击图片可放大查看）
                  </figcaption>
                </figure>
              );
            })}
          </div>

          <div className="mt-12 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 whitespace-nowrap cursor-pointer hover:text-primary-700"
            >
              <i className="ri-arrow-left-line leading-none"></i>
              返回产品资讯
            </Link>
            <span className="text-xs text-foreground-500">
              本文为演示示例内容，未接入真实数据接口
            </span>
          </div>
        </div>
      </article>

      {/* 相关文章 */}
      <section className="w-full bg-secondary-50">
        <div className="w-full px-4 md:px-10 py-16 md:py-20">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
                <span className="w-6 h-[2px] bg-accent-500"></span>
                相关文章
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight">
                继续阅读
              </h2>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((n) => (
              <NewsCard key={n.id} article={n} />
            ))}
          </div>
        </div>
      </section>

      {lightbox && (
        <ImageLightbox src={lightbox} alt={`${article.title} 配图放大`} onClose={() => setLightbox(null)} />
      )}
    </SiteLayout>
  );
}